import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import { fetchPlatformErrors, forgetPlatformFailedJob } from '../../services/platformErrorsApi';
import { mapLaravelError } from '../../services/platformTenantsApi';

const LOG_LIMIT = 50;

const levelColor = (level) => {
  const value = String(level || '').toUpperCase();
  if (value === 'WARNING') return 'warning';
  if (value === 'ERROR') return 'error';
  if (value === 'CRITICAL' || value === 'ALERT' || value === 'EMERGENCY') return 'error';
  return 'default';
};

const MetricCard = ({ label, value, helper }) => (
  <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
    <Stack spacing={0.75}>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="h5" sx={{ fontWeight: 800 }}>
        {value}
      </Typography>
      {helper ? (
        <Typography variant="caption" color="text.secondary">
          {helper}
        </Typography>
      ) : null}
    </Stack>
  </Paper>
);

const EmptyRow = ({ colSpan, label }) => (
  <TableRow>
    <TableCell colSpan={colSpan}>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
    </TableCell>
  </TableRow>
);

export default function SystemErrorsPage() {
  const [loading, setLoading] = useState(true);
  const [handlingId, setHandlingId] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [payload, setPayload] = useState(null);

  const loadErrors = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const data = await fetchPlatformErrors({ limit: LOG_LIMIT });
      setPayload(data || null);
    } catch (requestError) {
      setPayload(null);
      setError(mapLaravelError(requestError).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadErrors();
  }, [loadErrors]);

  const logs = useMemo(() => (Array.isArray(payload?.logs) ? payload.logs : []), [payload]);
  const failedJobs = useMemo(
    () => (Array.isArray(payload?.failed_jobs) ? payload.failed_jobs : []),
    [payload]
  );

  const handleForgetFailedJob = async (job) => {
    const identifier = job?.uuid || job?.id;
    if (!identifier) {
      return;
    }

    if (!window.confirm('Remove this failed job record?')) {
      return;
    }

    setHandlingId(String(identifier));
    setError('');
    setNotice('');

    try {
      const response = await forgetPlatformFailedJob(identifier);
      setNotice(response?.message || 'Failed job removed.');
      setPayload((current) => ({
        ...current,
        summary: {
          ...(current?.summary || {}),
          failed_jobs: Math.max(0, Number(current?.summary?.failed_jobs || 0) - 1),
        },
        failed_jobs: (current?.failed_jobs || []).filter((item) => String(item.uuid || item.id) !== String(identifier)),
      }));
    } catch (requestError) {
      setError(mapLaravelError(requestError).message);
    } finally {
      setHandlingId('');
    }
  };

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 } }}>
      <Stack spacing={2}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={1.5}
          justifyContent="space-between"
          alignItems={{ xs: 'stretch', md: 'center' }}
        >
          <Stack direction="row" spacing={1.25} alignItems="center">
            <ReportProblemOutlinedIcon color="error" />
            <Stack spacing={0.35}>
              <Typography variant="h5" sx={{ fontWeight: 800 }}>
                System Errors
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Recent sanitized application errors and failed queue jobs for super admin review.
              </Typography>
            </Stack>
          </Stack>

          <Button
            variant="contained"
            startIcon={<RefreshOutlinedIcon />}
            onClick={loadErrors}
            disabled={loading}
          >
            Refresh
          </Button>
        </Stack>

        {error ? <Alert severity="error">{error}</Alert> : null}
        {notice ? <Alert severity="success" onClose={() => setNotice('')}>{notice}</Alert> : null}

        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <MetricCard
              label="Recent log errors"
              value={payload?.summary?.log_errors ?? logs.length}
              helper={`Latest ${LOG_LIMIT} high-severity log entries`}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <MetricCard
              label="Failed jobs"
              value={payload?.summary?.failed_jobs ?? failedJobs.length}
              helper="Queue failures that can be removed after review"
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <MetricCard
              label="Environment"
              value={payload?.environment || '-'}
              helper={payload?.generated_at ? `Updated ${new Date(payload.generated_at).toLocaleString()}` : ''}
            />
          </Grid>
        </Grid>

        {loading ? (
          <Paper variant="outlined" sx={{ p: 3 }}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <CircularProgress size={22} />
              <Typography variant="body2" color="text.secondary">
                Loading system error summary...
              </Typography>
            </Stack>
          </Paper>
        ) : null}

        <Paper variant="outlined">
          <Box sx={{ p: 2, pb: 0 }}>
            <Typography variant="h6" sx={{ fontWeight: 800 }}>
              Recent Log Errors
            </Typography>
          </Box>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Time</TableCell>
                  <TableCell>Level</TableCell>
                  <TableCell>Message</TableCell>
                  <TableCell>Source</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {!loading && logs.length === 0 ? (
                  <EmptyRow colSpan={4} label="No recent warning or error log entries found." />
                ) : null}

                {logs.map((log) => (
                  <TableRow key={log.id || `${log.file}-${log.line}`} hover>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{log.display_time || '-'}</TableCell>
                    <TableCell>
                      <Chip size="small" color={levelColor(log.level)} label={log.level || 'UNKNOWN'} />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {log.message || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {log.file || '-'}{log.line ? `:${log.line}` : ''}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        <Paper variant="outlined">
          <Box sx={{ p: 2, pb: 0 }}>
            <Typography variant="h6" sx={{ fontWeight: 800 }}>
              Failed Queue Jobs
            </Typography>
          </Box>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Failed At</TableCell>
                  <TableCell>Queue</TableCell>
                  <TableCell>Exception</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {!loading && failedJobs.length === 0 ? (
                  <EmptyRow colSpan={4} label="No failed queue jobs found." />
                ) : null}

                {failedJobs.map((job) => {
                  const identifier = job.uuid || job.id;
                  const isHandling = handlingId === String(identifier);

                  return (
                    <TableRow key={identifier} hover>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{job.display_failed_at || '-'}</TableCell>
                      <TableCell>
                        <Stack spacing={0.25}>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            {job.queue || 'default'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {job.connection || '-'}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{job.exception || '-'}</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Remove failed job record">
                          <span>
                            <IconButton
                              color="error"
                              size="small"
                              onClick={() => handleForgetFailedJob(job)}
                              disabled={isHandling}
                            >
                              <DeleteOutlineOutlinedIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Stack>
    </Box>
  );
}
